import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import _ from "lodash"

import MenuItem from "@material-ui/core/MenuItem"
import { ListSubheader } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"

import widgetController from "../../api/widgetSlice"
import SelectInput from "../forms/SelectInput"
import useSmartFormat from "../../i18n/useSmartFormat"
import { words } from "../../i18n/lstrings"
import { CancelToken } from "../../api/httpClient"

function getSelectedOr(selector, orDefault) {
  return (selector && selector.selected) || orDefault
}

function getValues(selector, format) {
  if (_.isUndefined(selector) || _.isEmpty(selector?.values)) {
    return [{ key: -1, value: -1, text: format(words.none) }]
  }
  return selector.values
}

const useStyle = makeStyles((theme) => ({
  groupSelector: {
    color: theme.palette.common.gray,
    fontStyle: "italic"
  }
}))

const { selectorOf, selectedValueOf } = widgetController.select
const { actions: widgetActions } = widgetController

function FlatListSelector(props) {
  const format = useSmartFormat()
  const { id, selected, entries, onChange, ...others } = props
  return (
    <SelectInput
      {...others}
      label={format(others.label)}
      input={{
        name: `connected-selector-${id}`,
        value: selected,
        onChange,
        autoWidth: true
      }}
    >
      {entries.map((entry) => (
        <MenuItem key={entry.key} value={entry.value}>
          {entry.text}
        </MenuItem>
      ))}
    </SelectInput>
  )
}

FlatListSelector.propTypes = {
  id: PropTypes.string.isRequired,
  selected: PropTypes.any.isRequired,
  entries: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

function FlatListGroupingSelector(props) {
  const format = useSmartFormat()
  const { id, selected, entries, onChange, categories, ...others } = props
  return (
    <SelectInput
      {...others}
      label={format(others.label)}
      input={{
        name: `connected-selector-${id}`,
        value: selected,
        onChange,
        autoWidth: true
      }}
    >
      {categories?.map((item) => {
        const items = []
        items.push(<ListSubheader key={item.value}>{item.text}</ListSubheader>)
        _.forEach(entries, (entry) => {
          if (entry.customer === item.value) {
            items.push(
              <MenuItem key={entry.key} value={entry.value}>
                {" "}
                {entry.text}
              </MenuItem>
            )
          }
        })
        return items
      })}
    </SelectInput>
  )
}
FlatListGroupingSelector.propTypes = {
  id: PropTypes.string.isRequired,
  selected: PropTypes.any.isRequired,
  entries: PropTypes.array.isRequired,
  categories: PropTypes.array,
  onChange: PropTypes.func.isRequired
}
FlatListGroupingSelector.defaultProps = {
  categories: []
}
function GroupByListSelector(props) {
  const format = useSmartFormat()
  const classes = useStyle()
  const {
    id,
    selected,
    entries,
    onChange,
    groupBy,
    groupCategoryLabel,
    ...others
  } = props
  const groups = groupBy(entries)
  if (Object.keys(groups).length <= 1) {
    // when no groups funds, or at least one is undefined (not able to group ione item)
    // then fallback on flat selector
    return (
      <FlatListSelector
        id={id}
        selected={selected}
        entries={entries}
        onChange={onChange}
        {...others}
      />
    )
  }
  return (
    <SelectInput
      {...others}
      label={format(others.label)}
      input={{
        name: `connected-selector-${id}`,
        value: selected,
        onChange,
        autoWidth: true
      }}
    >
      {Object.keys(groupCategoryLabel).flatMap((groupKey) => {
        const groupCategoryLabel2 = groupCategoryLabel[groupKey]
        const groupItems = groups[groupKey]
        const children = []

        children.push(
          <ListSubheader
            key={groupCategoryLabel2}
            className={classes.groupSelector}
          >
            {format(groupCategoryLabel2)}
          </ListSubheader>
        )

        _.forEach(groupItems, (entry) =>
          children.push(
            <MenuItem key={entry.key} value={entry.value}>
              {entry.text}
            </MenuItem>
          )
        )

        return children
      })}
    </SelectInput>
  )
}

GroupByListSelector.propTypes = {
  id: PropTypes.string.isRequired,
  selected: PropTypes.any.isRequired,
  entries: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  groupBy: PropTypes.func.isRequired,
  groupCategoryLabel: PropTypes.object.isRequired
}

/**
 * A Selector impl connected to the Store.
 * It fetches available values, when selector value change it updates selected value in Store.
 * Voluntary does not cleanup values when unmount.
 *
 * If you are using onEmpty handler, then make sure it uses same ref, useCallback if necessary.
 * @param props
 * staticValue
 * @returns {JSX.Element}
 * @constructor
 */
export default function ConnectedSelector(props) {
  const { id, onEmpty, groupBy, groupCategoryLabel, staticValue, ...others } =
    props
  const format = useSmartFormat()
  const dispatch = useDispatch()
  const selector = useSelector(selectorOf(id))
  const defaultValue = useSelector(
    staticValue ? selectedValueOf(id) : () => undefined
  )

  // first effect to load selector values
  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    dispatch(
      widgetActions.fetchSelectorValues(id, token, groupBy, defaultValue)
    ).then((values) => {
      if (_.isEmpty(values)) {
        onEmpty(dispatch)
      }
    })

    return () => cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id])

  const onChange = (evt) => {
    dispatch(widgetActions.updateSelectorSelection(id, evt.target.value))
  }
  // handle default value while loading, and or when there are really No data
  const selected = getSelectedOr(selector, -1)
  const selectorEntries = getValues(selector, format)
  if (groupBy) {
    return (
      <GroupByListSelector
        id={id}
        selected={selected}
        entries={selectorEntries}
        onChange={onChange}
        groupBy={groupBy}
        groupCategoryLabel={groupCategoryLabel}
        {...others}
      />
    )
  }
  return (
    <FlatListSelector
      id={id}
      selected={selected}
      entries={selectorEntries}
      onChange={onChange}
      {...others}
    />
  )
}

ConnectedSelector.propTypes = {
  id: PropTypes.string.isRequired,
  onEmpty: PropTypes.func,
  groupBy: PropTypes.func,
  groupCategoryLabel: PropTypes.object,
  staticValue: PropTypes.bool,
  categories: PropTypes.object
}

ConnectedSelector.defaultProps = {
  onEmpty: _.noop,
  groupBy: undefined,
  groupCategoryLabel: undefined,
  staticValue: false,
  categories: {}
}
